import {
  AnchorButton,
  InlineChipWidget,
  LinkedMenuItemText,
  SIZE,
  StyledDescription,
} from '@/components/widgets/InlineChipWidget';
import { getTopicDescription } from '@/api/ChatApi';
import {
  mkTopicChipTrackAttrs,
  mkTopicDescriptionTrackAttrs,
  mkTopicMenuS2BtnTrackAttrs,
} from '@/analytics/props/inlineWidget';

import { Divider, Menu, MenuItem } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

interface InlineTopicChipWidgetProps {
  topicId: number | string;
  topicName: string;
  isDarkMode?: boolean;
}

export const InlineTopicChipWidget: FC<InlineTopicChipWidgetProps> = (
  props: InlineTopicChipWidgetProps,
) => {
  const { topicId, topicName, isDarkMode } = props;
  const [description, setDescription] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!topicId || !open) {
      setDescription(null);
      return;
    }
    if (description !== null) {
      return;
    }
    setIsLoading(true);
    getTopicDescription(topicId)
      .then((newDescription) => {
        setDescription(newDescription);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [topicId, open]);

  return (
    <>
      <AnchorButton onClick={handleClick}>
        <InlineChipWidget
          label={topicName}
          icon={<LightbulbIcon />}
          size={SIZE.medium}
          isMultiLine={false}
          aria-controls={open ? 'topic-chip-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          isDarkMode={isDarkMode}
          {...mkTopicChipTrackAttrs({ topicId })}
        />
      </AnchorButton>
      <Menu
        id="topic-chip-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {(description || isLoading) && (
          <StyledDescription
            key={topicId}
            {...mkTopicDescriptionTrackAttrs({ topicId, isLoading })}
          >
            {isLoading ? 'Loading...' : description}
          </StyledDescription>
        )}

        <Divider />
        <LinkedMenuItemText
          href={`https://topics-beta.apps.semanticscholar.org/topic/${topicId}`}
        >
          <MenuItem
            onClick={handleClose}
            {...mkTopicMenuS2BtnTrackAttrs({ topicId })}
          >
            Open Topic Page to Read More
          </MenuItem>
        </LinkedMenuItemText>
      </Menu>
    </>
  );
};
